import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, AlertCircle, Search, Filter, ArrowRight } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/auth/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.orders || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} className="text-[#10B981]" />;
      case 'processing': return <Clock size={16} className="text-[#3B82F6]" />;
      default: return <AlertCircle size={16} className="text-[#F97316]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30';
      case 'processing': return 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30';
      default: return 'bg-[#F97316]/20 text-[#F97316] border-[#F97316]/30';
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'all' || project.status === filter;
    const matchesSearch = project.order_number?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <DashboardLayout currentPage="/dashboard/projects">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="/dashboard/projects">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">My Projects</h1>
        <p className="text-white/50">Track and manage your project progress</p>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            data-testid="project-search"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#12121A] border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'processing', 'completed'].map((status) => (
            <button
              key={status}
              data-testid={`filter-${status}`}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all capitalize ${
                filter === status
                  ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white'
                  : 'bg-[#12121A] border border-white/10 text-white/60 hover:text-white hover:border-white/20'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-12 text-center">
          <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={40} className="text-white/20" />
          </div>
          <h3 className="text-white font-semibold mb-2">No projects found</h3>
          <p className="text-white/50 mb-6">Start your first project by browsing our services</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          >
            Browse Services
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              data-testid={`project-card-${project.id}`}
              className="bg-[#12121A] border border-white/10 rounded-2xl p-6 hover:border-[#3B82F6]/30 transition-all cursor-pointer group"
              onClick={() => navigate(`/dashboard/projects/${project.id}`)}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center border border-[#3B82F6]/30">
                    <Package size={24} className="text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1 group-hover:text-[#3B82F6] transition-colors">
                      {project.order_number}
                    </h3>
                    <p className="text-white/50 text-sm">
                      {project.items?.length || 0} service{project.items?.length !== 1 ? 's' : ''} • ${project.total?.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Progress */}
                  <div className="hidden md:block w-32">
                    <div className="flex justify-between text-xs text-white/40 mb-1">
                      <span>Progress</span>
                      <span>{project.status === 'completed' ? '100' : project.status === 'processing' ? '50' : '25'}%</span>
                    </div>
                    <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${project.status === 'completed' ? '100' : project.status === 'processing' ? '50' : '25'}%`,
                          background: 'linear-gradient(90deg, #3B82F6, #8B5CF6)'
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${getStatusColor(project.status)}`}>
                    {getStatusIcon(project.status)}
                    <span className="capitalize">{project.status}</span>
                  </div>

                  <ArrowRight size={20} className="text-white/40 group-hover:text-[#3B82F6] group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

export default Projects;
